import React from 'react';
import { Spinner } from 'react-bootstrap';
import cn from 'classnames';
import './loading.css';

type LoadingSize = 'sm' | 'md' | 'lg' | 'xl';
type LoadingVariant = 'border' | 'grow';

interface LoadingProps {
  size?: LoadingSize;
  variant?: LoadingVariant;
  text?: string;
  className?: string;
  overlay?: boolean;
  fullScreen?: boolean;
  color?: string;
  children?: React.ReactNode;
}

const Loading: React.FC<LoadingProps> = ({
  size = 'md',
  variant = 'border',
  text = 'Loading...',
  className = '',
  overlay = false,
  fullScreen = false,
  color = 'primary',
  children
}) => {
  const sizeMap = {
    sm: { width: '1rem', height: '1rem' },
    md: { width: '2rem', height: '2rem' },
    lg: { width: '3rem', height: '3rem' },
    xl: { width: '4rem', height: '4rem' }
  };

  const spinnerStyle = sizeMap[size];

  const loadingContent = (
    <div
      className={cn(
        'd-flex flex-column align-items-center justify-content-center',
        className
      )}
    >
      <Spinner
        animation={variant}
        variant={color}
        style={spinnerStyle}
        role='status'
        aria-hidden='true'
      />
      {text && (
        <div
          className='mt-2 text-muted'
          style={{ fontSize: size === 'sm' ? '0.875rem' : '1rem' }}
        >
          {text}
        </div>
      )}
      {children}
    </div>
  );
  if (fullScreen) {
    return (
      <div
        className='position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center loading-overlay'
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          zIndex: 9999
        }}
      >
        {loadingContent}
      </div>
    );
  }

  if (overlay) {
    return (
      <div
        className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center loading-overlay'
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          zIndex: 1000
        }}
      >
        {loadingContent}
      </div>
    );
  }

  return loadingContent;
};

// Specific loading components for common use cases
export const ButtonLoading: React.FC<{ size?: LoadingSize; text?: string }> = ({
  size = 'sm',
  text = 'Loading...'
}) => (
  <Loading
    size={size}
    text={text}
    className='px-3 py-1'
  />
);

export const PageLoading: React.FC<{ text?: string }> = ({
  text = 'Loading page...'
}) => (
  <Loading
    size='lg'
    text={text}
    className='my-5'
  />
);

export const ModalLoading: React.FC<{ text?: string }> = ({
  text = 'Processing...'
}) => (
  <Loading
    size='md'
    text={text}
    overlay
  />
);

export const FullScreenLoading: React.FC<{ text?: string }> = ({
  text = 'Loading...'
}) => (
  <Loading
    size='lg'
    text={text}
    fullScreen
  />
);

// Inline loading for small spaces
export const InlineLoading: React.FC<{ text?: string }> = ({ text }) => (
  <div className='d-inline-flex align-items-center'>
    <Spinner
      animation='border'
      size='sm'
      variant='primary'
      className='me-2'
      role='status'
      aria-hidden='true'
    />
    {text && <span className='text-muted'>{text}</span>}
  </div>
);

// Skeleton loading component
export const SkeletonLoading: React.FC<{
  width?: string | number;
  height?: string | number;
  className?: string;
  rounded?: boolean;
}> = ({ width = '100%', height = '1rem', className = '', rounded = false }) => (
  <div
    className={cn(
      'bg-light animate-pulse',
      { rounded: rounded, 'rounded-pill': !rounded },
      className
    )}
    style={{
      width,
      height,
      animation: 'pulse 1.5s ease-in-out infinite'
    }}
  />
);

export default Loading;
